import { Injectable, inject } from '@angular/core';
import { Observable, finalize, tap } from 'rxjs';

import {
  RequestPasswordResetRequest,
  ResetPasswordRequest,
  VerifyOtpRequest,
  VerifyOtpResponseDto,
} from '../../models/password-reset.dto';
import { AuthRepositoryContract } from '../repositories/auth.repository.contract';
import { AuthStore } from '../store/auth.store';
import { apiErrorMessage } from './api-error-message';

/**
 * The three calls of the password-reset flow, in one use case: they only make
 * sense in sequence and share the same loading/error bookkeeping, so splitting
 * them into three classes would triple the wiring for no gain.
 *
 * Each method returns its stream rather than subscribing, like LoginUseCase:
 * the page owns step navigation and its own in-flight flag; this owns the
 * store. Nothing here touches the session — a completed reset sends the
 * visitor to the login screen to sign in with the new password.
 */
@Injectable()
export class PasswordResetUseCase {
  private readonly repository = inject(AuthRepositoryContract);
  private readonly store = inject(AuthStore);

  /** Step 1 — ask the API to email a code to `email`. */
  requestCode(body: RequestPasswordResetRequest): Observable<string | undefined> {
    return this.track(
      this.repository.requestPasswordReset(body),
      'We could not send the code. Please check the email address and try again.',
    );
  }

  /** "Resend again" on step 2 — a fresh code for the same address. */
  resendCode(body: RequestPasswordResetRequest): Observable<string | undefined> {
    return this.track(
      this.repository.resendOtp(body),
      'We could not send a new code. Please try again in a moment.',
    );
  }

  /** Step 2 — check the code the visitor typed. */
  verifyCode(body: VerifyOtpRequest): Observable<VerifyOtpResponseDto> {
    return this.track(
      this.repository.verifyOtp(body),
      'That code is not valid or has expired. Please try again or request a new one.',
    );
  }

  /** Step 3 — set the new password. */
  setPassword(body: ResetPasswordRequest): Observable<void> {
    return this.track(
      this.repository.resetPassword(body),
      'We could not reset your password. Please try again.',
    );
  }

  private track<T>(request: Observable<T>, fallback: string): Observable<T> {
    this.store.setLoading(true);
    this.store.setError(null);

    return request.pipe(
      tap({
        error: (err: unknown) => this.store.setError(apiErrorMessage(err, fallback)),
      }),
      finalize(() => this.store.setLoading(false)),
    );
  }
}
